import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axiosInstance from "../../../../../AxiosInstance";
import usePolling from "../../../../../hooks/usePolling";
import { formatTimestamp } from "../../../../../utils/utils";
import styles from "./electrical_section.module.css";

const POLL_MS = 60000;
const RANGES = [1, 7, 30];
const STATUS_COLOR = { ok: "#16a34a", check: "#D97706", critical: "#DC2626", unknown: "#9ca3af" };

function fmtV(v) {
  return v === null || v === undefined ? "—" : `${Number(v).toFixed(1)} V`;
}

function fmtS(s) {
  if (s === null || s === undefined) return "—";
  const n = Number(s);
  if (n < 60) return `${n.toFixed(n < 10 ? 1 : 0)} s`;
  if (n < 3600) return `${Math.round(n / 60)} min`;
  return `${(n / 3600).toFixed(1)} h`;
}

function StatusChip({ status, t }) {
  return (
    <span className={styles.chip} style={{ background: STATUS_COLOR[status] || STATUS_COLOR.unknown }}>
      {t(`electrical.status.${status || "unknown"}`)}
    </span>
  );
}

function Tile({ title, status, value, sub, hint, t }) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileHead}>
        <span className={styles.tileTitle}>{title}</span>
        <StatusChip status={status} t={t} />
      </div>
      <div className={styles.tileValue}>{value}</div>
      {sub && <div className={styles.tileSub}>{sub}</div>}
      {hint && <div className={styles.tileHint}>{hint}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label, t }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipTitle}>{formatTimestamp(label)}</div>
      <div>{t("electrical.chart.min")}: {fmtV(p.v_min)}</div>
      <div>{t("electrical.chart.avg")}: {fmtV(p.v_avg)}</div>
      <div>{t("electrical.chart.max")}: {fmtV(p.v_max)}</div>
      {(p.dips || p.surges || p.hw_dips) ? (
        <div className={styles.tooltipEvents}>
          {t("electrical.chart.events", { dips: (p.dips || 0) + (p.hw_dips || 0), surges: p.surges || 0 })}
        </div>
      ) : null}
    </div>
  );
}

function describeEvent(e, t) {
  if (e.kind === "crank") {
    return t("electrical.events.crank", {
      vmin: fmtV(e.v_min), sag: fmtS(e.duration_s),
      recover: e.recover_s === null || e.recover_s === undefined ? t("electrical.events.noRecover") : fmtS(e.recover_s),
    });
  }
  if (e.kind === "sag") {
    return t("electrical.events.sag", { vmin: fmtV(e.v_min), before: fmtV(e.v_before), dur: fmtS(e.duration_s) });
  }
  return t("electrical.events.surge", { vmax: fmtV(e.v_max), dur: fmtS(e.duration_s) });
}

/**
 * Salud eléctrica del camión: batería en reposo, arranques, carga del
 * alternador y la serie continua de la tensión de entrada (mín/prom/máx por
 * minuto) con los eventos detectados por el equipo.
 */
export default function ElectricalSection({ pdui_id }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  usePolling(
    (isActive) =>
      axiosInstance
        .get(`pdui/${pdui_id}/electrical/?days=${days}`)
        .then((response) => {
          if (!isActive()) return;
          setData(response.data);
          setError(false);
        })
        .catch(() => {
          if (isActive()) setError(true);
        }),
    POLL_MS,
    true,
    `${pdui_id}-${days}`,
  );

  const health = data?.health || {};
  const battery = health.battery || {};
  const crank = health.crank || {};
  const alternator = health.alternator || {};
  const transients = health.transients_24h || {};
  const nominal = data?.nominal_v || 24;
  const series = (data?.series || []).map((p) => ({ ...p, t: new Date(p.t).getTime() }));
  const yMin = Math.floor(Math.min(nominal * 0.6, ...series.map((p) => p.v_min ?? nominal)) - 1);
  const yMax = Math.ceil(Math.max(nominal * 1.25, ...series.map((p) => p.v_max ?? nominal)) + 1);

  return (
    <div className={styles.container}>
      <div className={styles.header}>{t("electrical.title")}</div>
      <div className={styles.help}>{t("electrical.help", { nominal })}</div>
      {error && <div className={styles.error}>{t("electrical.error")}</div>}
      {data && !data.has_data && <div className={styles.empty}>{t("electrical.empty")}</div>}

      <div className={styles.tiles}>
        <Tile
          t={t}
          title={t("electrical.tiles.battery")}
          status={battery.status}
          value={battery.soc_pct === null || battery.soc_pct === undefined ? "—" : `≈ ${Math.round(battery.soc_pct)} %`}
          sub={battery.v_avg !== undefined && battery.v_avg !== null
            ? t("electrical.tiles.batterySub", { v: fmtV(battery.v_avg), i: battery.i_avg === null || battery.i_avg === undefined ? "—" : `${Number(battery.i_avg).toFixed(1)} A` })
            : null}
          hint={battery.at ? t("electrical.tiles.at", { when: formatTimestamp(battery.at) }) : t("electrical.tiles.batteryHint")}
        />
        <Tile
          t={t}
          title={t("electrical.tiles.crank")}
          status={crank.status}
          value={fmtV(crank.v_min)}
          sub={crank.v_min !== undefined && crank.v_min !== null
            ? t("electrical.tiles.crankSub", { sag: fmtS(crank.duration_s), recover: crank.recover_s === null || crank.recover_s === undefined ? "—" : fmtS(crank.recover_s) })
            : null}
          hint={crank.at ? t("electrical.tiles.at", { when: formatTimestamp(crank.at) }) : t("electrical.tiles.crankHint")}
        />
        <Tile
          t={t}
          title={t("electrical.tiles.alternator")}
          status={alternator.status}
          value={fmtV(alternator.v_charging_avg)}
          sub={alternator.charging_pct !== undefined && alternator.charging_pct !== null
            ? t("electrical.tiles.alternatorSub", { pct: Math.round(alternator.charging_pct), dur: fmtS(alternator.duration_s) })
            : null}
          hint={alternator.at ? t("electrical.tiles.at", { when: formatTimestamp(alternator.at) }) : t("electrical.tiles.alternatorHint")}
        />
        <Tile
          t={t}
          title={t("electrical.tiles.transients")}
          status={!data?.has_data ? "unknown" : (transients.dips || 0) + (transients.surges || 0) + (transients.hw_dips || 0) >= 20 ? "check" : "ok"}
          value={`${(transients.dips || 0) + (transients.hw_dips || 0)} / ${transients.surges || 0}`}
          sub={t("electrical.tiles.transientsSub")}
          hint={t("electrical.tiles.transientsHint")}
        />
      </div>

      <div className={styles.chartCard}>
        <div className={styles.chartHead}>
          <div className={styles.chartTitle}>{t("electrical.chart.title")}</div>
          <div className={styles.ranges}>
            {RANGES.map((d) => (
              <button
                key={d}
                type="button"
                className={`${styles.range} ${days === d ? styles.rangeOn : ""}`}
                onClick={() => setDays(d)}
              >
                {t(`electrical.ranges.d${d}`)}
              </button>
            ))}
          </div>
        </div>
        {series.length === 0 ? (
          <div className={styles.chartEmpty}>{t("electrical.chart.empty")}</div>
        ) : (
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#eef0f3" vertical={false} />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={(v) => formatTimestamp(v).slice(days === 1 ? 11 : 0, days === 1 ? 16 : 10)}
                  tick={{ fontSize: 10, fill: "#6B7280" }}
                  minTickGap={40}
                />
                <YAxis domain={[yMin, yMax]} tick={{ fontSize: 10, fill: "#6B7280" }} width={34} unit="" />
                <Tooltip content={<ChartTooltip t={t} />} />
                <Area dataKey="v_max" stroke="none" fill="#c7d2fe" fillOpacity={0.6} isAnimationActive={false} />
                <Area dataKey="v_min" stroke="none" fill="#ffffff" fillOpacity={1} isAnimationActive={false} />
                <Line dataKey="v_avg" stroke="#1B365D" strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className={styles.legend}>
              <span><i className={styles.swatchBand} /> {t("electrical.chart.band")}</span>
              <span><i className={styles.swatchLine} /> {t("electrical.chart.avg")}</span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.block}>
        <div className={styles.blockTitle}>{t("electrical.events.title")}</div>
        {(data?.events || []).length === 0 ? (
          <div className={styles.empty}>{t("electrical.events.empty")}</div>
        ) : (
          <div className={styles.list}>
            {data.events.map((e, i) => (
              <div key={`${e.kind}-${e.created_at}-${i}`} className={`${styles.event} ${styles[`event_${e.kind}`] || ""}`}>
                <div className={styles.eventWhen}>{formatTimestamp(e.t_start || e.created_at)}</div>
                <div className={styles.eventKind}>{t(`electrical.events.kind.${e.kind}`)}</div>
                <div className={styles.eventText}>{describeEvent(e, t)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.block}>
        <div className={styles.blockTitle}>{t("electrical.trips.title")}</div>
        {(data?.trips || []).length === 0 ? (
          <div className={styles.empty}>{t("electrical.trips.empty")}</div>
        ) : (
          <div className={styles.list}>
            {data.trips.map((tr, i) => (
              <div key={`${tr.created_at}-${i}`} className={styles.event}>
                <div className={styles.eventWhen}>{formatTimestamp(tr.t_start || tr.created_at)}</div>
                <div className={styles.eventKind}>{fmtS(tr.duration_s)}</div>
                <div className={styles.eventText}>
                  {t("electrical.trips.row", {
                    v: fmtV(tr.v_charging_avg ?? tr.v_avg),
                    pct: Math.round(tr.charging_pct || 0),
                    dips: tr.dips || 0,
                    surges: tr.surges || 0,
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
